import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { XCircle, ArrowLeft, HelpCircle } from 'lucide-react';

export default function OfferCancel() {
  const { offerId } = useParams<{ offerId: string }>();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Cancellation Message */}
        <Card className="mb-6 border-yellow-200 bg-yellow-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <XCircle className="w-12 h-12 text-yellow-600 flex-shrink-0" />
              <div>
                <h1 className="text-2xl font-bold text-yellow-900 mb-2">
                  Payment Cancelled
                </h1>
                <p className="text-yellow-700">
                  Your payment was not completed. Your spot in this route offer is still available.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* What Happened */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>What Happened?</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              You cancelled the payment process or the session expired. Don't worry - your offer is still valid
              and you can try again whenever you're ready.
            </p>
            <p className="text-gray-600">
              Your selected weeks and pricing remain the same. No changes have been made to your offer.
            </p>
          </CardContent>
        </Card>

        {/* Next Steps */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>What's Next?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <ArrowLeft className="w-5 h-5 text-shuttlebee-yellow flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-sm">Return to Offer</p>
                  <p className="text-sm text-gray-600">
                    Go back to the offer page to review details and try payment again.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <HelpCircle className="w-5 h-5 text-shuttlebee-yellow flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-sm">Need Help?</p>
                  <p className="text-sm text-gray-600">
                    If you're experiencing issues with payment, please contact us at{' '}
                    <a
                      href="mailto:hello@getshuttlebee.com"
                      className="text-shuttlebee-dark underline hover:no-underline"
                    >
                      hello@getshuttlebee.com
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex gap-4">
          <Button
            onClick={() => navigate(`/offers/${offerId}`)}
            className="flex-1"
          >
            Return to Offer
          </Button>
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="flex-1"
          >
            Go to Dashboard
          </Button>
        </div>

        {/* Help Text */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>
            Questions? Email us at{' '}
            <a
              href="mailto:hello@getshuttlebee.com"
              className="text-shuttlebee-dark underline hover:no-underline"
            >
              hello@getshuttlebee.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
